import maplibregl from 'maplibre-gl';
import { getLotRepresentativeLatLng } from '../hooks/useLotSelection';
import { pickNonEmptyBoundary } from './enrichSharedLotsForMap';

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseCoord = (coord) => {
  if (!coord) return null;
  if (Array.isArray(coord)) {
    const lng = toFiniteNumber(coord[0]);
    const lat = toFiniteNumber(coord[1]);
    if (lng == null || lat == null) return null;
    return { lng, lat };
  }
  const lng = toFiniteNumber(coord.lng ?? coord.lon ?? coord.longitude);
  const lat = toFiniteNumber(coord.lat ?? coord.latitude);
  if (lng == null || lat == null) return null;
  return { lng, lat };
};

const getLotPoints = (lot) => {
  const boundary = Array.isArray(lot?.boundary) ? lot.boundary : [];
  const boundaryPoints = boundary.map(parseCoord).filter(Boolean);

  if (boundaryPoints.length > 0) return boundaryPoints;

  const lat = toFiniteNumber(lot?.lat);
  const lng = toFiniteNumber(lot?.lng);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return [{ lat, lng }];

  return [];
};

/** true si el lote trae un perímetro usable para fitBounds (no solo un pin). */
export const hasUsableBoundary = (lot) => {
  const boundary = pickNonEmptyBoundary(lot?.boundary);
  if (!boundary) return false;
  return boundary.filter(parseCoord).length >= 3;
};

/** Mínimo de mapa visible para que fitBounds no reciba padding mayor al canvas. */
const MIN_VISIBLE_FIT_PX = 96;
/** Caja mínima alrededor de un pin para respetar padding (flyTo ignora el panel). */
const POINT_FIT_DELTA_DEG = 0.00015;

const scalePaddingAxis = (a, b, size) => {
  const max = Math.max(0, size - MIN_VISIBLE_FIT_PX);
  const sum = a + b;
  if (sum <= max) return [a, b];
  if (sum <= 0) return [0, 0];
  const factor = max / sum;
  return [Math.floor(a * factor), Math.floor(b * factor)];
};

/**
 * Recorta padding de fitBounds para que siempre quede un hueco usable en el canvas.
 * En mobile un left de panel desktop (~560px) supera el ancho y el encuadre no corre.
 */
export const clampMapFitPadding = (map, padding) => {
  if (padding == null) return 70;
  if (typeof padding === 'number') return padding;
  const el = typeof map?.getContainer === 'function' ? map.getContainer() : null;
  const width = el?.clientWidth || 0;
  const height = el?.clientHeight || 0;
  const next = {
    top: Number(padding.top) || 0,
    right: Number(padding.right) || 0,
    bottom: Number(padding.bottom) || 0,
    left: Number(padding.left) || 0,
  };
  if (width < 1 || height < 1) return next;
  [next.left, next.right] = scalePaddingAxis(next.left, next.right, width);
  [next.top, next.bottom] = scalePaddingAxis(next.top, next.bottom, height);
  return next;
};

const applyFitBounds = (map, bounds, options = {}) => {
  const fitOpts = {
    padding: clampMapFitPadding(map, options.padding ?? 70),
    duration: options.duration ?? 700,
  };
  if (options.maxZoom != null) fitOpts.maxZoom = options.maxZoom;
  try {
    map.fitBounds(bounds, fitOpts);
    return true;
  } catch (_) {
    return false;
  }
};

const waitForMapLayout = () => new Promise((resolve) => {
  requestAnimationFrame(() => requestAnimationFrame(resolve));
});

/** Tras abrir dock/panel: esperar layout y resize antes de fitBounds. */
export const prepareMapForFit = async (map) => {
  if (!map) return false;
  await waitForMapLayout();
  try {
    map.resize();
  } catch (_) {
    // mapa destruyéndose
  }
  return true;
};

/** Fila enriquecida para encuadre (coords de unidades + boundary de polygonInfo). */
export const enrichLotRowForLocate = (row, polygonInfo) => {
  if (!row) return null;
  const boundary = pickNonEmptyBoundary(polygonInfo?.boundary, row?.boundary);
  const rep = getLotRepresentativeLatLng({
    ...row,
    boundary: boundary || row?.boundary,
    id: row.lot_id || row.id,
  });
  return {
    ...row,
    boundary: boundary || row?.boundary || null,
    lat: rep?.lat ?? polygonInfo?.lat ?? row?.lat ?? null,
    lng: rep?.lng ?? polygonInfo?.lng ?? row?.lng ?? null,
  };
};

/**
 * Encuadra el mapa a UN lote como al seleccionar una parcela normal:
 * - con boundary → fitBounds al perímetro (cualquier tamaño)
 * - sin boundary → fitBounds a una caja mínima alrededor del pin (honra padding)
 * Devuelve true si pudo mover el mapa.
 */
export const fitMapToLotBounds = (map, lot, options = {}) => {
  if (!map || !lot) return false;

  const rep = getLotRepresentativeLatLng({
    ...lot,
    id: lot.lot_id || lot.id,
  });
  const row = {
    ...lot,
    lat: rep?.lat ?? lot.lat,
    lng: rep?.lng ?? lot.lng,
  };

  const boundary = pickNonEmptyBoundary(row.boundary);
  const bounds = new maplibregl.LngLatBounds();
  let hasArea = false;
  (boundary || []).forEach((coord) => {
    const parsed = parseCoord(coord);
    if (!parsed) return;
    bounds.extend([parsed.lng, parsed.lat]);
    hasArea = true;
  });

  if (hasArea) {
    return applyFitBounds(map, bounds, {
      padding: options.padding,
      duration: options.duration ?? 700,
      maxZoom: options.maxZoom,
    });
  }

  const lng = toFiniteNumber(row.lng);
  const lat = toFiniteNumber(row.lat);
  if (lng != null && lat != null) {
    // fitBounds (no flyTo) para honrar padding: el pin queda en el mapa visible
    // y no centrado detrás del bottom sheet / panel.
    const pinBounds = new maplibregl.LngLatBounds(
      [lng - POINT_FIT_DELTA_DEG, lat - POINT_FIT_DELTA_DEG],
      [lng + POINT_FIT_DELTA_DEG, lat + POINT_FIT_DELTA_DEG],
    );
    return applyFitBounds(map, pinBounds, {
      padding: options.padding,
      duration: options.duration ?? 700,
      maxZoom: options.pointZoom ?? options.maxZoom ?? 17,
    });
  }

  return false;
};

/**
 * Encuadre fiable al seleccionar desde listas:
 * 1) boundary en fila → fitBounds
 * 2) si no → fetch catastro → fitBounds
 * 3) fallback pin
 */
export const locateLotOnMap = async (map, {
  lotId,
  candidates = [],
  fetchLotById,
  fitOptions = {},
  isCancelled = () => false,
} = {}) => {
  if (!map || !lotId || isCancelled()) return false;
  await prepareMapForFit(map);
  if (isCancelled()) return false;

  const unique = [];
  const seen = new Set();
  candidates.filter(Boolean).forEach((row) => {
    const key = String(row.lot_id || row.id || '');
    if (!key || seen.has(key)) return;
    seen.add(key);
    unique.push(row);
  });

  for (const row of unique) {
    if (isCancelled()) return false;
    if (hasUsableBoundary(row) && fitMapToLotBounds(map, row, fitOptions)) return true;
  }

  if (typeof fetchLotById === 'function') {
    const fetched = await fetchLotById(lotId);
    if (isCancelled()) return false;
    await prepareMapForFit(map);
    if (isCancelled()) return false;
    if (fetched && fitMapToLotBounds(map, fetched, fitOptions)) return true;
  }

  for (const row of unique) {
    if (isCancelled()) return false;
    if (fitMapToLotBounds(map, row, fitOptions)) return true;
  }

  return false;
};

export const fitMapBoundsToLots = (map, lots, options = {}) => {
  if (!map || !Array.isArray(lots) || lots.length === 0) return false;

  const bounds = new maplibregl.LngLatBounds();
  let hasPoints = false;

  lots.forEach((lot) => {
    const points = getLotPoints(lot);
    points.forEach((point) => {
      bounds.extend([point.lng, point.lat]);
      hasPoints = true;
    });
  });

  if (!hasPoints) return false;

  return applyFitBounds(map, bounds, {
    padding: options.padding,
    duration: options.duration ?? 900,
    maxZoom: options.maxZoom ?? 16.5,
  });
};

export default fitMapBoundsToLots;
